import { useMemo } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  FormHelperText,
  Stack,
  Switch,
  TextField,
  Typography,
  Box
} from "@mui/material";
import LockResetIcon from "@mui/icons-material/LockReset";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useCountryOptions } from "../utils/countries";
import type { UserGroupSummary } from "../types/user";
import PersonIcon from "@mui/icons-material/Person";

type UserEditDialogProps = {
  open: boolean;
  user: {
    id: string;
    username: string;
    email: string;
    name: string;
    surname: string;
    company: string;
    country: string;
    role: string;
    active: boolean;
    userGroups: string[];
  } | null;
  existingUsers: Array<{
    id: string;
    username: string;
    email: string;
  }>;
  availableUserGroups: UserGroupSummary[];
  authorizedFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  currentUserId?: string | null;
  onClose: () => void;
  onResetPassword?: (user: {
    id: string;
    username: string;
    email: string;
    name: string;
    surname: string;
    company: string;
    country: string;
    role: string;
    active: boolean;
    userGroups: string[];
  }) => void;
  onSuccess?: (result: {
    message: string;
    user: {
      id: string;
      username: string;
      email: string;
      name: string;
      surname: string;
      company: string;
      country: string;
      role: string;
      active: boolean;
      userGroups: string[];
    };
  }) => void;
};

type FormValues = {
  username: string;
  email: string;
  name: string;
  surname: string;
  company: string;
  country: string;
  role: string;
  active: boolean;
  userGroups: string[];
};

const UserEditDialog = ({
  open,
  user,
  existingUsers,
  availableUserGroups,
  authorizedFetch,
  onClose,
  onSuccess,
  currentUserId,
  onResetPassword
}: UserEditDialogProps) => {
  const countryOptions = useCountryOptions();
  const userGroupMap = useMemo(() => {
    const entries = availableUserGroups.map((group) => [group.id, group.name] as const);
    return new Map(entries);
  }, [availableUserGroups]);
  const handleDialogClose = (_event?: unknown, reason?: string) => {
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      return;
    }
    onClose();
  };

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        username: Yup.string()
          .trim()
          .required("Username is required")
          .test("unique-username", "Username already exists", (value) => {
            if (!value) {
              return true;
            }
            const normalized = value.trim().toLowerCase();
            const currentId = user?.id ?? "";
            return !existingUsers.some(
              (candidate) =>
                candidate.id !== currentId && candidate.username.trim().toLowerCase() === normalized
            );
          }),
        email: Yup.string()
          .trim()
          .email("Enter a valid email")
          .required("Email is required")
          .test("unique-email", "Email already exists", (value) => {
            if (!value) {
              return true;
            }
            const normalized = value.trim().toLowerCase();
            const currentId = user?.id ?? "";
            return !existingUsers.some(
              (candidate) =>
                candidate.id !== currentId && candidate.email.trim().toLowerCase() === normalized
            );
          }),
        country: Yup.string()
          .trim()
          .test("country-length", "Country must be a 2-letter code", (value) => {
            if (!value) return true;
            const trimmed = value.trim();
            return trimmed.length === 2;
          }),
        role: Yup.string()
          .trim()
          .required("Role is required")
          .oneOf(["admin", "user"], "Invalid role")
      }),
    [existingUsers, user?.id]
  );

  return (
    <Dialog open={open} onClose={handleDialogClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Box display="inline-flex" alignItems="center" gap={1}>
          <PersonIcon sx={{ color: "text.secondary" }} />
          Edit user
        </Box>
      </DialogTitle>
      <Formik<FormValues>
        key={user?.id ?? "user-edit"}
        enableReinitialize
        initialValues={{
          username: user?.username ?? "",
          email: user?.email ?? "",
          name: user?.name ?? "",
          surname: user?.surname ?? "",
          company: user?.company ?? "",
          country: user?.country ?? "",
          role: user?.role ?? "user",
          active: user?.active ?? false,
          userGroups: user?.userGroups ?? []
        }}
        validationSchema={validationSchema}
        onSubmit={async (values, helpers) => {
          if (!user) {
            helpers.setSubmitting(false);
            return;
          }

          helpers.setStatus(undefined);
          const normalizedUserGroups = Array.from(
            new Set(
              values.userGroups
                .map((group) => group.trim())
                .filter((group) => group.length > 0)
            )
          );
          const trimmedValues = {
            username: values.username.trim(),
            email: values.email.trim(),
            name: values.name.trim(),
            surname: values.surname.trim(),
            company: values.company.trim(),
            country: values.country.trim().toUpperCase(),
            role: values.role.trim().toLowerCase() === "admin" ? "admin" : "user",
            active: values.active,
            userGroups: normalizedUserGroups
          };

          try {
            const response = await authorizedFetch(`/api/users/${user.id}`, {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                ...trimmedValues,
                userGroups: trimmedValues.userGroups
              })
            });

            if (!response.ok) {
              const payload = await response.json().catch(() => null);
              const message = typeof payload?.error === "string" ? payload.error : "Unable to update user.";
              helpers.setStatus(message);
              return;
            }

            onSuccess?.({
              message: `${trimmedValues.username} was updated.`,
              user: {
                id: user.id,
                username: trimmedValues.username,
                email: trimmedValues.email,
                name: trimmedValues.name,
                surname: trimmedValues.surname,
                company: trimmedValues.company,
                country: trimmedValues.country,
                role: trimmedValues.role,
                active: trimmedValues.active,
                userGroups: trimmedValues.userGroups
              }
            });
            onClose();
          } catch (error) {
            console.error(error);
            helpers.setStatus("Unable to update user. Please try again.");
          } finally {
            helpers.setSubmitting(false);
          }
        }}
      >
        {({ values, errors, touched, handleChange, handleBlur, isSubmitting, isValid, status, setFieldValue }) => {
          const trimmedUsername = values.username.trim();
          const trimmedEmail = values.email.trim();
          const trimmedName = values.name.trim();
          const trimmedSurname = values.surname.trim();
          const trimmedCompany = values.company.trim();
          const trimmedCountry = values.country.trim().toUpperCase();
          const trimmedRole = values.role.trim().toLowerCase();
          const selectedCountry =
            countryOptions.find((option) => option.code === trimmedCountry) ?? null;
          const normalizedUserGroups = Array.from(
            new Set(
              values.userGroups
                .map((group) => group.trim())
                .filter((group) => group.length > 0)
            )
          );
          const currentUsername = user?.username ?? "";
          const currentEmail = user?.email ?? "";
          const currentName = user?.name ?? "";
          const currentSurname = user?.surname ?? "";
          const currentCompany = user?.company ?? "";
          const currentCountry = (user?.country ?? "").toUpperCase();
          const currentRole = (user?.role ?? "").toLowerCase();
          const currentGroups = user?.userGroups ?? [];
          const groupsChanged =
            normalizedUserGroups.length !== currentGroups.length ||
            normalizedUserGroups.some((group) => !currentGroups.includes(group));
          const hasChanges =
            trimmedUsername !== currentUsername ||
            trimmedEmail !== currentEmail ||
            trimmedName !== currentName ||
            trimmedSurname !== currentSurname ||
            trimmedCompany !== currentCompany ||
            trimmedCountry !== currentCountry ||
            trimmedRole !== currentRole ||
            groupsChanged ||
            values.active !== (user?.active ?? false);
          const editingSelf = currentUserId != null && user?.id === currentUserId;

          return (
            <Form noValidate>
              <DialogContent>
                <Stack spacing={2}>
                  {status ? <Alert severity="error">{status}</Alert> : null}
                  <Stack direction="row" spacing={2} alignItems="center">
                    <TextField
                      name="username"
                      label="Username"
                      fullWidth
                      value={values.username}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.username && Boolean(errors.username)}
                      helperText={touched.username ? errors.username : undefined}
                      autoFocus
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          name="active"
                          checked={values.active}
                          onChange={(_event, checked) => setFieldValue("active", checked)}
                          color="primary"
                          disabled={editingSelf}
                        />
                      }
                      label={values.active ? "Active" : "Disabled"}
                      sx={{ m: 0, flexShrink: 0 }}
                    />
                  </Stack>
                  <TextField
                    name="email"
                    label="Email"
                    fullWidth
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email ? errors.email : undefined}
                    type="email"
                  />
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      name="name"
                      label="Name"
                      fullWidth
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <TextField
                      name="surname"
                      label="Surname"
                      fullWidth
                      value={values.surname}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </Stack>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      name="company"
                      label="Company"
                      fullWidth
                      sx={{ flex: 1 }}
                      value={values.company}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <Autocomplete
                      fullWidth
                      sx={{ flexBasis: { xs: "100%", sm: "40%" } }}
                      options={countryOptions}
                      value={selectedCountry}
                      onChange={(_event, option) => setFieldValue("country", option?.code ?? "")}
                      getOptionLabel={(option) => option.name}
                      isOptionEqualToValue={(option, value) => option.code === value.code}
                      renderOption={(props, option) => (
                        <li {...props} key={option.code}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {option.Flag ? <option.Flag width={22} /> : null}
                            <Box>
                              <Typography variant="body2">{option.name}</Typography>
                            </Box>
                          </Box>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Country"
                          placeholder="Select country"
                          onBlur={handleBlur}
                          error={touched.country && Boolean(errors.country)}
                          helperText={touched.country ? errors.country : undefined}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: selectedCountry?.Flag ? (
                              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 0.5 }}>
                                <selectedCountry.Flag width={20} />
                                {params.InputProps.startAdornment}
                              </Box>
                            ) : (
                              params.InputProps.startAdornment
                            )
                          }}
                        />
                      )}
                      clearOnBlur
                    />
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ width: "100%" }}>
                    <FormControl size="small" disabled={editingSelf} sx={{ minWidth: 160 }}>
                      <InputLabel id="edit-user-role-label">Role</InputLabel>
                      <Select
                        labelId="edit-user-role-label"
                        label="Role"
                        name="role"
                        value={values.role}
                        onChange={(event) => setFieldValue("role", event.target.value)}
                        onBlur={handleBlur}
                        error={touched.role && Boolean(errors.role)}
                      >
                        <MenuItem value="admin">Admin</MenuItem>
                        <MenuItem value="user">User</MenuItem>
                      </Select>
                      {touched.role && errors.role ? <FormHelperText error>{errors.role}</FormHelperText> : null}
                    </FormControl>
                    <Button
                      variant="outlined"
                      startIcon={<LockResetIcon />}
                      onClick={() => user && onResetPassword?.(user)}
                      disabled={!user || !onResetPassword}
                      size="small"
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      Reset password
                    </Button>
                  </Stack>
                  {values.role === "admin" ? (
                    <Typography variant="body2" color="text.secondary">
                      Admins have access to all user groups.
                    </Typography>
                  ) : (
                    <FormControl
                      fullWidth
                      size="small"
                      disabled={availableUserGroups.length === 0}
                    >
                      <InputLabel id="edit-user-groups-label">User groups</InputLabel>
                      <Select
                        multiple
                        labelId="edit-user-groups-label"
                        label="User groups"
                        name="userGroups"
                        value={values.userGroups}
                        onChange={(event) => {
                          const raw = event.target.value;
                          const next = Array.isArray(raw) ? raw : [raw];
                          setFieldValue(
                            "userGroups",
                            Array.from(
                              new Set(
                                next
                                  .map((group) => String(group).trim())
                                  .filter((group) => group.length > 0)
                              )
                            )
                          );
                        }}
                        renderValue={(selected) => {
                          if (!selected.length) {
                            return "None";
                          }
                          return selected
                            .map((id) => userGroupMap.get(String(id)) ?? String(id))
                            .join(", ");
                        }}
                      >
                        {availableUserGroups.length === 0 ? (
                          <MenuItem disabled value="__none">
                            No user groups
                          </MenuItem>
                        ) : (
                          availableUserGroups.map((group) => (
                            <MenuItem key={group.id} value={group.id}>
                              <Checkbox checked={values.userGroups.includes(group.id)} />
                              <ListItemText primary={group.name} />
                            </MenuItem>
                          ))
                        )}
                      </Select>
                  {availableUserGroups.length === 0 ? (
                    <FormHelperText>No user groups available.</FormHelperText>
                  ) : null}
                </FormControl>
              )}
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button onClick={onClose} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button type="submit" variant="contained" disabled={!hasChanges || !isValid || isSubmitting}>
                  Save
                </Button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default UserEditDialog;
